import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Star } from 'lucide-react';
import { templates } from '../data/templates';
import GetTemplateModal from './GetTemplateModal';

// 6 hand-picked templates that showcase the breadth of the catalogue.
const FEATURED_IDS = ['watch', 'realestate', 'saas', 'wedding', 'restaurant', 'agency'];

const FeaturedTemplates = () => {
  const { t: tr } = useTranslation();
  const [bought, setBought] = useState(null);
  const featured = FEATURED_IDS.map((id) => templates.find((t) => t.id === id)).filter(Boolean);

  return (
    <section data-testid="featured-templates" id="templates" className="relative bg-[#FAF8F5] py-24 sm:py-32 overflow-hidden">
      {/* Section masthead — luxury editorial */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-[1px] w-10 bg-[#C9A96E]" />
          <span className="text-[8px] lg:text-[9px] font-semibold tracking-[0.32em] uppercase text-[#8A857D]">— Ready-to-launch templates · 28 plates available</span>
        </div>

        <div className="grid lg:grid-cols-[1fr_auto] items-end gap-6 mb-12">
          <h2 className="font-sans font-bold text-[#1A1816] tracking-[-0.025em] leading-[0.95] text-[44px] sm:text-6xl lg:text-7xl">
            {tr('common.trophy_headline1')} <span className="font-serif italic text-[#C9A96E]">{tr('common.trophy_headline1b')}</span><br />
            {tr('common.trophy_headline2')}
          </h2>
          <p className="font-serif text-base lg:text-lg text-[#8A857D] max-w-md leading-[1.65]">
            {tr('common.trophy_sub')}
          </p>
        </div>
      </div>

      {/* Curated 6-template editorial grid · tighter, more polished plates */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {featured.map((t, i) => (
            <motion.article
              key={t.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.7, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
              className="group relative bg-white border border-[#1A1816]/10 hover:border-[#C9A96E]/60 hover:shadow-[0_24px_48px_-28px_rgba(26,24,22,0.35)] transition-all duration-500"
              data-testid={`featured-card-${t.id}`}
            >
              {/* Plate — wider 16:10 ratio + soft inner padding for a more refined editorial feel */}
              <div className="relative p-2.5 bg-[#F5EDE4]">
                <div className="aspect-[16/10] overflow-hidden bg-[#EFE6DA]">
                  <img
                    src={t.thumb}
                    alt={`${t.name} — ${t.industry}`}
                    className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-1000"
                    loading="lazy"
                  />
                </div>
                {/* Tier badge — Signature ✦ vs Premium ◆ */}
                <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-[#1A1816]/85 backdrop-blur-sm text-[#FAF8F5] px-2.5 py-1 font-mono text-[10px] sm:text-[9px] tracking-[0.32em] uppercase">
                  {t.tier === 'premium' ? <span className="text-[#C9A96E]">◆</span> : <span className="text-[#C9A96E]"><Star className="w-2.5 h-2.5 fill-[#C9A96E]" /></span>}
                  <span>{t.tier === 'premium' ? 'Premium' : 'Signature'}</span>
                </div>
              </div>
              {/* Caption block — tighter padding, smaller heading */}
              <div className="px-4 py-4 sm:px-5 sm:py-5 border-t border-[#1A1816]/08">
                <div className="flex items-baseline justify-between gap-3 font-mono text-[10px] sm:text-[9px] tracking-[0.32em] uppercase text-[#8A857D]">
                  <span>— {t.industry.split('/')[0].trim()}</span>
                  <span className="text-[#C9A96E]">{t.price}</span>
                </div>
                <h3 className="font-serif italic text-xl sm:text-2xl text-[#1A1816] mt-2 leading-[1.05]">{t.name}</h3>
                <p className="text-sm text-[#8A857D] mt-1.5 leading-[1.55] line-clamp-2">{(t.description || '').replace(/&amp;/g, '&').replace(/&apos;/g, "'").split('. ')[0]}.</p>
                <div className="mt-4 flex items-center gap-2">
                  <Link
                    to={`/templates/${t.id}/preview`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`featured-preview-${t.id}`}
                    className="flex-1 bg-[#1A1816] text-[#FAF8F5] py-2 font-mono text-[11px] sm:text-[10px] tracking-[0.32em] uppercase hover:bg-[#C9A96E] transition-colors flex items-center justify-center gap-2"
                    aria-label={`Preview ${t.name}`}
                  >
                    <ArrowUpRight className="w-4 h-4" />
                    {tr('common.view_live')}
                  </Link>
                  <button
                    onClick={() => setBought(t)}
                    data-testid={`featured-buy-${t.id}`}
                    className="border border-[#1A1816]/20 hover:border-[#C9A96E] hover:bg-[#C9A96E]/10 px-3 py-2 transition-colors font-mono text-[10px] tracking-[0.32em] uppercase text-[#1A1816]"
                    aria-label={`${tr('common.contact')} ${t.name}`}
                  >
                    {tr('common.contact')}
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* "View all 28" CTA */}
        <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-5 pt-9 border-t border-[#1A1816]/10">
          <p className="font-serif italic text-2xl lg:text-3xl text-[#1A1816]">22 more templates inside the catalogue.</p>
          <Link to="/templates" data-testid="featured-cta-all" className="group inline-flex items-center gap-3 bg-[#C9A96E] text-[#1A1816] px-7 py-3.5 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#1A1816] hover:text-[#FAF8F5] transition-colors">
            <span>{tr('common.open_catalogue_full')}</span>
            <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
          </Link>
        </div>
      </div>

      {bought && <GetTemplateModal template={bought} onClose={() => setBought(null)} />}
    </section>
  );
};

export default FeaturedTemplates;

